import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/sonner';
import { BillingPresenter } from '@/components/billing/billing.presenter';
import { mapBillingApiToSnapshot } from '@/components/billing/map-billing-api-to-snapshot';
import { mapBillingToViewModel } from '@/components/billing/map-billing-to-view-model';
import { useBilling } from '@/hooks/use-billing';
import { billingStatusQueryKey } from '@/hooks/use-billing-status';
import { useTenant } from '@/hooks/use-tenant';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  CenteredContent,
  CenteredContentLayout,
  Header,
} from '@/layouts/centered-content.layout';
import { SidebarLayout } from '@/layouts/sidebar.layout';
import { payTenantPayment } from '@/lib/billing-mutations';

export default function BillingPage() {
  const { LL, locale } = useI18nContext();
  const tenant = useTenant();
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useBilling();

  const billing = useMemo(
    () =>
      data
        ? mapBillingToViewModel(mapBillingApiToSnapshot(data), locale, LL)
        : null,
    [data, locale, LL]
  );

  const payMutation = useMutation({
    mutationFn: (paymentId: string) => payTenantPayment(tenant.slug, paymentId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['billing', tenant.slug],
      });
      await queryClient.invalidateQueries({
        queryKey: billingStatusQueryKey(tenant.slug),
      });
      toast.success(LL.billingPage.paySuccess());
    },
    onError: () => {
      toast.error(LL.billingPage.payFailed());
    },
  });

  if (isLoading || isError || !billing) {
    return (
      <SidebarLayout className="!p-0">
        <CenteredContentLayout>
          <Header>
            <h1 className="text-lg tracking-tight sm:text-xl">
              {LL.billingPage.title()}
            </h1>
          </Header>

          <CenteredContent>
            <div className="py-10 text-center">
              <p className="text-muted-foreground">
                {isLoading
                  ? LL.billingPage.loading()
                  : LL.billingPage.failedToLoad()}
              </p>
            </div>
          </CenteredContent>
        </CenteredContentLayout>
      </SidebarLayout>
    );
  }

  return (
    <BillingPresenter
      {...billing}
      isPayingPayment={payMutation.isPending}
      onPayWithStoredCard={(paymentId) => payMutation.mutate(paymentId)}
    />
  );
}
